import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import {
  validateFpSpReferral,
  validatePartnerReferral,
  type FpSpReferralResponse,
  type PartnerReferralResponse,
} from '../api/referralApi';
import { fetchInvitePublic, type InvitePublicResponse } from '../api/inviteApi';
import { trackRumAction } from '@/shared/observability/rum';
import { ensureAnonSessionId } from './useSignupContext';

/**
 * Tracks pre-login attribution for the anonymous visitor:
 * - Referral code that brought them (FP/SP individual or partner company)
 * - Email-invite metadata (Item 6)
 *
 * Architecture: the resolution side effects (URL parsing, public API calls,
 * RUM session_start events) run ONCE in `UnauthAttributionProvider`. Consumer
 * components use `useUnauthAttribution()` which reads from the shared context
 * — no per-consumer useEffect, no race conditions when multiple cards/tabs
 * mount at the same time. Spec linhas 146-150, 264.
 */

const STORAGE_KEY = 'bquest_unauth_attribution';

export type ReferralType = 'FP' | 'SP' | 'PARTNER';

export type UnauthAttribution = {
  referralCode?: string;
  providerName?: string;
  partnerCompanyName?: string;
  referralType?: ReferralType;
  // Email-invite fields (Item 6)
  inviteId?: string;
  inviterName?: string;
  inviterCompany?: string;
  /** Role of the inviter — used by the modal to skip Screen 2 when an FA invited. */
  inviterRole?: 'FINANCIAL_ADVISOR' | 'SERVICE_PROVIDER';
  inviteEmail?: string;
  inviteFirstName?: string;
  inviteLastName?: string;
};

const readAttribution = (): UnauthAttribution | null => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as UnauthAttribution) : null;
  } catch {
    return null;
  }
};

const writeAttribution = (attribution: UnauthAttribution) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(attribution));
  } catch {
    // localStorage unavailable — attribution lost for this session.
  }
};

const clearAttribution = () => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
};

/** Extract referral codes and invite token from a URL query string. */
const extractFromUrl = (
  search: string,
): { fpSpCode?: string; partnerCode?: string; inviteId?: string } => {
  const params = new URLSearchParams(search);
  // FP/SP referral arrives via /signup?code=X or /signup?refCode=X (both redirected to /)
  const fpSpCode = params.get('code') ?? params.get('refCode') ?? undefined;
  // Partner-company referral arrives via /partner-signup?referralCode=X
  const partnerCode = params.get('referralCode') ?? undefined;
  // Care-seeker invite arrives via /signup/:inviteId → redirected to /?inviteId=X
  const inviteId = params.get('inviteId') ?? undefined;
  return { fpSpCode, partnerCode, inviteId };
};

const fpSpResponseToAttribution = (
  code: string,
  data: FpSpReferralResponse,
): UnauthAttribution => ({
  referralCode: code,
  providerName: data.professionalName,
  partnerCompanyName: data.companyName,
  referralType: data.role === 'FINANCIAL_ADVISOR' ? 'FP' : 'SP',
});

const partnerResponseToAttribution = (
  code: string,
  data: PartnerReferralResponse,
): UnauthAttribution => ({
  referralCode: code,
  partnerCompanyName: data.name,
  referralType: 'PARTNER',
});

const inviteResponseToAttribution = (
  data: InvitePublicResponse,
): UnauthAttribution => ({
  inviteId: data.id,
  inviteEmail: data.email,
  inviteFirstName: data.firstName,
  inviteLastName: data.lastName,
  inviterName: data.inviterName,
  inviterCompany: data.inviterCompany,
  inviterRole: data.inviterRole,
});

/**
 * Strips referral-specific fields from a cached attribution so a new referral
 * resolution can populate them cleanly. Without this, switching from an FP/SP
 * code (which sets `providerName`) to a partner code (which does not) would
 * leave a stale `providerName` in localStorage and downstream RUM properties.
 */
const stripReferralFields = (attribution: UnauthAttribution): UnauthAttribution => {
  const {
    referralCode: _code,
    providerName: _provider,
    partnerCompanyName: _partner,
    referralType: _type,
    ...rest
  } = attribution;
  return rest;
};

type UnauthAttributionContextValue = {
  attribution: UnauthAttribution | null;
  update: (patch: Partial<UnauthAttribution>) => void;
  clear: () => void;
};

const UnauthAttributionContext =
  createContext<UnauthAttributionContextValue | null>(null);

/**
 * Mounts the single attribution-resolution effect for the unauth tree. Mount
 * ONCE at the top of the guest layout (`AuthAwareLayout`) so the URL-param
 * parse, public API calls (validateFpSpReferral / validatePartnerReferral /
 * fetchInvitePublic), and `unauth.referral_session_start` /
 * `unauth.invite_session_start` RUM events all fire exactly once per session
 * — even when many descendant components consume `useUnauthAttribution()`.
 */
export const UnauthAttributionProvider = ({ children }: { children: ReactNode }) => {
  const [attribution, setAttribution] = useState<UnauthAttribution | null>(() =>
    readAttribution(),
  );

  useEffect(() => {
    const { fpSpCode, partnerCode, inviteId } = extractFromUrl(window.location.search);

    // No params in URL → nothing to do; cached value (if any) stays.
    if (!fpSpCode && !partnerCode && !inviteId) return;

    const cached = readAttribution();
    const cachedCode = cached?.referralCode;
    const cachedInvite = cached?.inviteId;
    const newCode = fpSpCode ?? partnerCode;
    // Skip if both the code and inviteId already match what's cached.
    const codeUnchanged = !newCode || newCode === cachedCode;
    const inviteUnchanged = !inviteId || inviteId === cachedInvite;
    if (codeUnchanged && inviteUnchanged) return;

    const resolve = async () => {
      try {
        // Start from the existing cache so referral + invite can coexist
        // (e.g. someone arriving via /signup/:inviteId?code=X — unlikely but possible).
        let merged: UnauthAttribution = cached ?? {};
        let referralResolved = false;
        let inviteResolved = false;

        if (fpSpCode && fpSpCode !== cachedCode) {
          const data = await validateFpSpReferral(fpSpCode);
          if (data) {
            // Strip first so stale fields from a prior referral type don't survive.
            merged = {
              ...stripReferralFields(merged),
              ...fpSpResponseToAttribution(fpSpCode, data),
            };
            referralResolved = true;
          } else {
            // Partner-company codes get shared in the FP/SP link format
            // (?refCode= / ?code=) often enough that dropping them here loses
            // real attribution. Retry the same code as a partner code before
            // giving up.
            const partnerData = await validatePartnerReferral(fpSpCode);
            if (partnerData) {
              merged = {
                ...stripReferralFields(merged),
                ...partnerResponseToAttribution(fpSpCode, partnerData),
              };
              referralResolved = true;
            }
          }
        } else if (partnerCode && partnerCode !== cachedCode) {
          const data = await validatePartnerReferral(partnerCode);
          if (data) {
            merged = {
              ...stripReferralFields(merged),
              ...partnerResponseToAttribution(partnerCode, data),
            };
            referralResolved = true;
          }
        }

        if (inviteId && inviteId !== cachedInvite) {
          const data = await fetchInvitePublic(inviteId);
          if (data) {
            merged = { ...merged, ...inviteResponseToAttribution(data) };
            inviteResolved = true;
          }
        }

        // Skip the write/setState when neither referral nor invite resolved —
        // otherwise an invalid code (validateFpSpReferral returns null) would
        // persist `{}` into localStorage and clobber the `null` initial state.
        if (!referralResolved && !inviteResolved) {
          return;
        }

        writeAttribution(merged);
        setAttribution(merged);

        // Fire session_start events only on first successful resolution (spec linhas 172-173).
        // The cachedCode/cachedInvite guards above prevent re-firing on remount; the
        // singleton provider mount prevents concurrent duplicates from sibling consumers.
        const anonSessionId = ensureAnonSessionId();
        if (referralResolved) {
          trackRumAction('unauth.referral_session_start', {
            anonSessionId,
            referralCode: merged.referralCode,
            referralType: merged.referralType,
            providerName: merged.providerName,
            partnerCompanyName: merged.partnerCompanyName,
          });
        }
        if (inviteResolved) {
          trackRumAction('unauth.invite_session_start', {
            anonSessionId,
            inviteId: merged.inviteId,
            inviterType: merged.inviterRole,
            inviterName: merged.inviterName,
            inviterCompany: merged.inviterCompany,
          });
        }
      } catch {
        // Network error — leave cache untouched. The visitor can still sign up
        // without attribution; we just lose the FP linkage / invite prefill.
      }
    };

    void resolve();
    // attribution intentionally not in deps — we only react to URL changes; cache is read at top.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const update = useCallback((patch: Partial<UnauthAttribution>) => {
    const next = { ...(readAttribution() ?? {}), ...patch };
    writeAttribution(next);
    setAttribution(next);
  }, []);

  const clear = useCallback(() => {
    clearAttribution();
    setAttribution(null);
  }, []);

  return (
    <UnauthAttributionContext.Provider value={{ attribution, update, clear }}>
      {children}
    </UnauthAttributionContext.Provider>
  );
};

/**
 * When called outside `UnauthAttributionProvider` (e.g. ProviderCard rendered
 * in the authenticated AppLayout, which doesn't mount the unauth provider),
 * returns `attribution: null` and no-op update/clear. `useUnauthRumAction`
 * then emits events without referral/invite enrichment — the desired behavior
 * for authenticated callers.
 */
const NOOP_VALUE: UnauthAttributionContextValue = {
  attribution: null,
  update: () => {},
  clear: () => {},
};

export const useUnauthAttribution = (): UnauthAttributionContextValue => {
  return useContext(UnauthAttributionContext) ?? NOOP_VALUE;
};
